import React from 'react';
import I18n from 'i18n-js';
import { tv } from 'tailwind-variants';
import readingIcon from '@/../images/icons/reading.svg';
import calendarPlusIcon from 'images/icons/calendar-plus.svg';
import calendarCheckedIcon from 'images/icons/calendar-checked.svg';
import useFlags from '@/hooks/useFlags';
import useLessonOverviewContext from '@/components/shared/NewLessonOverview/useLessonOverviewContext';
import { TCITooltip } from '@/components/ui/Tooltip';
import Text from '@/components/ui/Text';
import AssignmentMadeTooltipContent from '@/components/common/AssignmentMadeTooltipContent';
import { ConditionalWrap } from '@/components/common/Utils';
import { CARD_HEIGHT_REFLOW } from '@/components/shared/NewLessonOverview/reflowStyles';
import { IReferenceMaterial } from '../types';

type CardProps = {
  referenceMaterial?: IReferenceMaterial;
  openAssign: () => void;
  setInitialValues: (any) => void;
};

const cardStyles = tv({
  base: [
    'tw-border tw-border-solid tw-border-med-grey tw-shadow-lg tw-max-w-3xl',
    'tw-rounded-lg tw-flex tw-justify-start tw-items-center tw-bg-white',
  ],
  variants: {
    reflow: {
      false: 'tw-h-[4.5rem]',
      true: CARD_HEIGHT_REFLOW,
    },
  },
});

const ReferenceMaterialCard = ({ referenceMaterial, openAssign, setInitialValues }: CardProps) => {
  const { accessibilityReflow } = useFlags();
  const isStaffer = useLessonOverviewContext(state => state.isStaffer);
  const icon = referenceMaterial.isAssigned ? calendarCheckedIcon : calendarPlusIcon;

  const onAssignmentButtonClick = () => {
    if (referenceMaterial.isAssigned && setInitialValues) return;

    setInitialValues({
      modelType: referenceMaterial.type,
      modelsToAssign: [{
        label: referenceMaterial.label,
        score: referenceMaterial.maxScore,
        value: `ReferenceMaterial-${referenceMaterial.id}`,
      }],
    });
    openAssign();
  };

  return (
    <div className={cardStyles({ reflow: accessibilityReflow })}>
      <div className="tw-flex tw-justify-center tw-items-center tw-rounded-lg tw-bg-blue tw-min-h-[2.5rem]
          tw-min-w-[2.5rem] tw-mx-[1rem]"
      >
        <img alt="book-icon" className="tw-invert" src={readingIcon} />
      </div>
      <Text asChild className="tw-font-bold" size="sm" variant="body">
        <a className="tw-mb-0 tw-text-tci-purple tw-w-[67%] tw-mr-auto" href={referenceMaterial.href}>
          {`${referenceMaterial.libraryLabel}: ${referenceMaterial.label}`}
        </a>
      </Text>
      {isStaffer && (
        <ConditionalWrap
          condition={referenceMaterial.isAssigned && isStaffer}
          wrap={children => (
            <TCITooltip side="bottom">
              <TCITooltip.Trigger asChild>
                {children}
              </TCITooltip.Trigger>
              <TCITooltip.Content>
                <AssignmentMadeTooltipContent />
              </TCITooltip.Content>
            </TCITooltip>
          )}
        >
          <button
            aria-label={I18n.t('create_assignment')}
            className="tw-bg-transparent tw-border-none focus:tw-border-none active:tw-border-none tw-cursor-pointer
              focus:tw-outline-none"
            type="button"
            onClick={onAssignmentButtonClick}
          >
            <img
              alt="calendar-plus icon"
              className="tw-mr-[1rem]"
              src={icon}
            />
          </button>
        </ConditionalWrap>
      )}
    </div>
  );
};
export default ReferenceMaterialCard;
